import { createSlice } from "@reduxjs/toolkit";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { parseReportConfiguration } from "../../components/builder/utils/configuration/configurations.ts";
import type { CurrentReportActionTypes, CurrentReportState } from "../types/currentReportState.types";
import { CURRENT_REPORT_SLICE_NAME } from "../utils/storeUtils";

const initialState: CurrentReportState = {
  refreshToken: "",
  previewMode: "maximize",
  editMode: false,
  sessionId: generateGuid(),
  cacheSessionId: generateGuid(),
  transactional: true,
};

export const currentReportSlice = createSlice({
  name: CURRENT_REPORT_SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    refreshSession: (state) => {
      state.cacheSessionId = generateGuid();
    },
    refresh: (state) => {
      state.refreshToken = generateGuid();
      state.cacheSessionId = generateGuid();
    },
    updateCurrentReportType: (state, action: CurrentReportActionTypes["updateCurrentReportType"]) => {
      if (state.report) {
        state.report.reportType = action.payload;
      }
    },
    setCurrentReport: (state, action: CurrentReportActionTypes["setCurrentReport"]) => {
      state.report = {
        ...action.payload,
        configuration: parseReportConfiguration(action.payload.configuration, action.payload.reportType),
      };
    },
    setAuthorizationInfo: (state, action: CurrentReportActionTypes["setAuthorizationInfo"]) => {
      state.authorizationInfo = action.payload;
    },
    updateReport: (state, action: CurrentReportActionTypes["updateReport"]) => {
      state.report = Object.assign({}, state.report, action.payload);
      if (action.payload.configuration) {
        state.report.configuration = parseReportConfiguration(action.payload.configuration, state.report.reportType);
      }
    },
    updatePreviewMode: (state, action: CurrentReportActionTypes["updatePreviewMode"]) => {
      state.previewMode = action.payload;
    },
    updateReportMode: (state, action: CurrentReportActionTypes["updateReportMode"]) => {
      state.editMode = action.payload;
    },
    setAutoShowItemOptionsId: (state, action: CurrentReportActionTypes["setAutoShowItemOptionsId"]) => {
      state.autoShowItemOptionsId = action.payload;
    },
    setEtag(state, action: CurrentReportActionTypes["setEtag"]) {
      state.etag = action.payload.etag;
      state.cacheValid = action.payload.cacheValid;
    },
    setSessionId: (state, action: CurrentReportActionTypes["setSessionId"]) => {
      state.sessionId = action.payload;
    },
    applyTemplateOnReport: (state, action: CurrentReportActionTypes["applyTemplateOnReport"]) => {
      if (state.report) {
        state.report.reportType = action.payload.reportType;
        state.report.dataSetId = action.payload.dataSetId;
        state.cacheSessionId = generateGuid();
        state.resetSessionId = generateGuid();
      }
    },
    updateBuildProgress: (state, action: CurrentReportActionTypes["updateBuildProgress"]) => {
      state.currentBuildProgress = action.payload;
    },
    updateIsReportTransactional: (state, action: CurrentReportActionTypes["updateIsReportTransactional"]) => {
      state.transactional = action.payload;
    },
    updateReportDataSetId: (state, action: CurrentReportActionTypes["updateReportDataSetId"]) => {
      if (state.report) {
        state.report.dataSetId = action.payload;
      }
    },
  },
});

export default currentReportSlice.reducer;
